import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
	Backdrop,
	Box,
	Button,
	IconButton,
	Stack,
	Typography
} from "@mui/material";
import {
	Menu,
	Close,
	Home,
	ReceiptLong,
	Inventory2,
	Description,
	ListAlt,
	AccountBalanceWallet,
	Handshake,
	People
} from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import { LogoutButton } from "../Shared/LogoutButton";

const navigationItems = [
	{ label: "Home", icon: <Home />, navigateTo: "/" },
	{
		label: "Invoices",
		icon: <ReceiptLong />,
		navigateTo: "/search-invoice"
	},
	{ label: "Inventory", icon: <Inventory2 />, navigateTo: "/items" },
	{
		label: "Settlement",
		icon: <Description />,
		navigateTo: "/invoice-and-returns"
	},
	{ label: "Ledger", icon: <Description />, navigateTo: "/ledger" },
	{
		label: "Cashbook",
		icon: <AccountBalanceWallet />,
		navigateTo: "/shop-cash"
	},
	{
		label: "Investments",
		icon: <Handshake />,
		navigateTo: "/investment-tracker"
	}
] as const;

interface AppLayoutProps {
	children: React.ReactNode;
}

const AppLayout: React.FC<AppLayoutProps> = ({ children }) => {
	const navigate = useNavigate();
	const { user, userRole } = useAuth();
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

	const username = user?.displayName ?? user?.email ?? "User";

	const handleNavigate = (path: string) => {
		navigate(path);
		setMobileMenuOpen(false);
	};

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				bgcolor: "background.default",
				color: "text.primary",
				position: "relative"
			}}
		>
			<Backdrop
				open={mobileMenuOpen}
				onClick={() => setMobileMenuOpen(false)}
				sx={{ display: { md: "none" }, zIndex: 15 }}
			/>
			<IconButton
				onClick={() => setMobileMenuOpen(true)}
				sx={{
					display: { xs: "inline-flex", md: "none" },
					position: "fixed",
					top: 16,
					left: 16,
					zIndex: 25,
					bgcolor: "background.paper",
					boxShadow: 2
				}}
				aria-label="Open menu"
			>
				<Menu />
			</IconButton>
			<Box
				sx={{
					width: { xs: mobileMenuOpen ? 280 : 0, md: 260 },
					overflow: "hidden",
					borderRight: { xs: 0, md: 1 },
					borderColor: "divider",
					bgcolor: "background.paper",
					position: { xs: "fixed", md: "sticky" },
					left: 0,
					top: 0,
					height: "100vh",
					zIndex: 20,
					transform: {
						xs: mobileMenuOpen
							? "translateX(0)"
							: "translateX(-100%)",
						md: "translateX(0)"
					},
					transition:
						"transform 0.25s ease, width 0.25s ease, opacity 0.2s ease",
					boxShadow: { xs: mobileMenuOpen ? 6 : 0, md: 0 },
					px: { xs: mobileMenuOpen ? 2 : 0, md: 2 },
					py: { xs: mobileMenuOpen ? 2.5 : 0, md: 2.5 },
					display: "flex",
					flexDirection: "column",
					gap: 3,
					pointerEvents: {
						xs: mobileMenuOpen ? "auto" : "none",
						md: "auto"
					},
					opacity: { xs: mobileMenuOpen ? 1 : 0, md: 1 },
					flexShrink: 0
				}}
			>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between"
					}}
				>
					<Box
						sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
					>
						<Box
							sx={{
								width: 36,
								height: 36,
								borderRadius: 2,
								bgcolor: "primary.main",
								color: "white",
								display: "grid",
								placeItems: "center",
								fontWeight: 800
							}}
						>
							F
						</Box>
						<Box>
							<Typography variant="subtitle1" fontWeight={800}>
								FashionBazi Billing
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								Workspace
							</Typography>
						</Box>
					</Box>
					<IconButton
						onClick={() => setMobileMenuOpen(false)}
						sx={{ display: { xs: "inline-flex", md: "none" } }}
						aria-label="Close menu"
					>
						<Close />
					</IconButton>
				</Box>

				<Box>
					<Typography
						variant="overline"
						color="text.secondary"
						sx={{ px: 1.5, mb: 1 }}
					>
						Workspace
					</Typography>
					<Stack spacing={0.5}>
						{navigationItems.slice(0, 3).map((item) => (
							<Button
								key={item.label}
								variant="text"
								startIcon={item.icon}
								onClick={() => handleNavigate(item.navigateTo)}
								sx={{
									justifyContent: "flex-start",
									color: "text.secondary",
									borderRadius: 2,
									px: 1.5
								}}
							>
								{item.label}
							</Button>
						))}
					</Stack>
				</Box>

				<Box>
					<Typography
						variant="overline"
						color="text.secondary"
						sx={{ px: 1.5, mb: 1 }}
					>
						Reports
					</Typography>
					<Stack spacing={0.5}>
						{navigationItems.slice(3).map((item) => (
							<Button
								key={item.label}
								variant="text"
								startIcon={item.icon}
								onClick={() => handleNavigate(item.navigateTo)}
								sx={{
									justifyContent: "flex-start",
									color: "text.secondary",
									borderRadius: 2,
									px: 1.5
								}}
							>
								{item.label}
							</Button>
						))}
					</Stack>
				</Box>

				{userRole === "admin" && (
					<Box>
						<Typography
							variant="overline"
							color="text.secondary"
							sx={{ px: 1.5, mb: 1 }}
						>
							Admin
						</Typography>
						<Stack spacing={0.5}>
							<Button
								variant="text"
								startIcon={<ListAlt />}
								onClick={() => handleNavigate("/audit-logs")}
								sx={{
									justifyContent: "flex-start",
									color: "text.secondary",
									borderRadius: 2,
									px: 1.5
								}}
							>
								Audit Logs
							</Button>
							<Button
								variant="text"
								startIcon={<People />}
								onClick={() => handleNavigate("/manage-users")}
								sx={{
									justifyContent: "flex-start",
									color: "text.secondary",
									borderRadius: 2,
									px: 1.5
								}}
							>
								Manage Users
							</Button>
						</Stack>
					</Box>
				)}

				<Box
					sx={{
						mt: "auto",
						borderTop: 1,
						borderColor: "divider",
						pt: 2
					}}
				>
					<Typography variant="subtitle2" fontWeight={700}>
						{username}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						Administrator
					</Typography>
					<LogoutButton fullWidth sx={{ mt: 1.5 }}>
						Log out
					</LogoutButton>
				</Box>
			</Box>

			<Box
				component="main"
				sx={{
					flex: 1,
					minWidth: 0,
					px: { xs: 2, md: 3, lg: 4 },
					py: { xs: 2, md: 3 }
				}}
			>
				{children}
			</Box>
		</Box>
	);
};

export default AppLayout;
